<script setup lang="ts">
import { ref } from 'vue'
import { useCookieConsent } from '@/composables/useCookieConsent'

const { showBanner, bannerAttention, acceptAll, rejectNonEssential, savePreferences, consent } =
  useCookieConsent()

const showSettings = ref(false)
const analyticsChoice = ref(consent.value.analytics)
const marketingChoice = ref(consent.value.marketing)

function openSettings() {
  analyticsChoice.value = consent.value.analytics
  marketingChoice.value = consent.value.marketing
  showSettings.value = true
}

function saveAndClose() {
  savePreferences({ analytics: analyticsChoice.value, marketing: marketingChoice.value })
  showSettings.value = false
}
</script>

<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="showBanner"
        role="dialog"
        aria-modal="false"
        aria-labelledby="cookie-banner-title"
        aria-describedby="cookie-banner-description"
        class="fixed bottom-0 left-0 right-0 z-[9999] p-4 sm:p-6"
      >
        <div
          class="mx-auto max-w-4xl bg-white shadow-2xl rounded-2xl border border-gray-200 overflow-hidden"
          :class="{ 'banner-attention': bannerAttention }"
        >
          <!-- Attention note: shown when an action (e.g. registration) is
               blocked until the user makes a cookie choice -->
          <div
            v-if="bannerAttention"
            class="flex items-center gap-2 px-5 sm:px-6 py-2.5 bg-amber-50 border-b border-amber-200 text-amber-800 text-xs sm:text-sm font-medium"
          >
            <i class="fas fa-hand-pointer"></i>
            Please choose your cookie preferences to continue with registration.
          </div>
          <!-- Main banner -->
          <div v-if="!showSettings" class="p-5 sm:p-6">
            <div class="flex flex-col sm:flex-row sm:items-start gap-4">
              <div class="flex-1">
                <h2
                  id="cookie-banner-title"
                  class="text-base sm:text-lg font-semibold text-gray-900 mb-2"
                >
                  We value your privacy
                </h2>
                <p
                  id="cookie-banner-description"
                  class="text-xs sm:text-sm text-gray-600 leading-relaxed"
                >
                  We use essential cookies to make Manito work. With your consent,
                  we also use analytics cookies to understand how the site is used
                  and marketing cookies to measure our advertising. You can change
                  your choice at any time.
                  <router-link
                    to="/privacy"
                    class="text-teal-600 hover:text-teal-700 underline font-medium"
                  >
                    Read our Privacy Policy
                  </router-link>
                  .
                </p>
              </div>
            </div>
            <div class="mt-5 flex flex-col sm:flex-row gap-2 sm:justify-end">
              <button
                type="button"
                @click="openSettings"
                class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Settings
              </button>
              <button
                type="button"
                @click="rejectNonEssential"
                class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Reject non-essential
              </button>
              <button
                type="button"
                @click="acceptAll"
                class="px-4 py-2 text-sm font-medium text-white bg-teal-600 border border-teal-600 rounded-lg hover:bg-teal-700 transition-colors"
              >
                Accept all
              </button>
            </div>
          </div>

          <!-- Settings panel -->
          <div v-else class="p-5 sm:p-6">
            <h2 class="text-base sm:text-lg font-semibold text-gray-900 mb-4">
              Cookie settings
            </h2>

            <div class="space-y-4">
              <!-- Essential -->
              <div class="flex items-start justify-between gap-4 p-4 bg-gray-50 rounded-lg">
                <div class="flex-1">
                  <h3 class="text-sm font-semibold text-gray-900">Essential cookies</h3>
                  <p class="text-xs text-gray-600 mt-1">
                    Required for the service to function (login session, security,
                    load balancing). Always active.
                  </p>
                </div>
                <div class="flex items-center">
                  <span
                    class="text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Always on
                  </span>
                </div>
              </div>

              <!-- Analytics -->
              <div class="flex items-start justify-between gap-4 p-4 bg-gray-50 rounded-lg">
                <div class="flex-1">
                  <h3 class="text-sm font-semibold text-gray-900">Analytics cookies</h3>
                  <p class="text-xs text-gray-600 mt-1">
                    Help us understand how visitors use the site (Google Analytics).
                    No personal profiles are built.
                  </p>
                </div>
                <label class="relative inline-flex items-center cursor-pointer">
                  <input
                    v-model="analyticsChoice"
                    type="checkbox"
                    class="sr-only peer"
                  />
                  <div
                    class="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"
                  ></div>
                </label>
              </div>

              <!-- Marketing -->
              <div class="flex items-start justify-between gap-4 p-4 bg-gray-50 rounded-lg">
                <div class="flex-1">
                  <h3 class="text-sm font-semibold text-gray-900">Marketing cookies</h3>
                  <p class="text-xs text-gray-600 mt-1">
                    Used to measure the effectiveness of our advertising
                    (Meta Pixel). Helps us understand which campaigns work.
                  </p>
                </div>
                <label class="relative inline-flex items-center cursor-pointer">
                  <input
                    v-model="marketingChoice"
                    type="checkbox"
                    class="sr-only peer"
                  />
                  <div
                    class="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"
                  ></div>
                </label>
              </div>
            </div>

            <div class="mt-5 flex flex-col sm:flex-row gap-2 sm:justify-end">
              <button
                type="button"
                @click="showSettings = false"
                class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Back
              </button>
              <button
                type="button"
                @click="saveAndClose"
                class="px-4 py-2 text-sm font-medium text-white bg-teal-600 border border-teal-600 rounded-lg hover:bg-teal-700 transition-colors"
              >
                Save preferences
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

/* Draws the eye when an action is blocked pending a cookie decision */
.banner-attention {
  animation: banner-shake 0.5s ease-in-out;
  box-shadow:
    0 0 0 3px rgba(245, 158, 11, 0.45),
    0 25px 50px -12px rgba(0, 0, 0, 0.25);
  border-color: #f59e0b;
}

@keyframes banner-shake {
  0%,
  100% {
    transform: translateX(0);
  }
  20% {
    transform: translateX(-8px);
  }
  40% {
    transform: translateX(8px);
  }
  60% {
    transform: translateX(-5px);
  }
  80% {
    transform: translateX(5px);
  }
}
</style>
